import * as React from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/utils/cn";
import { useTranslation } from "react-i18next";
import { calculateQuotaUsagePercent, formatQuotaUsagePercent } from "../utils";

type StatCardProps = {
  label: string;
  value: React.ReactNode;
  valueTooltip?: string;
  displayValueIsZero?: boolean;
  used?: number;
  limit?: number;
  limitLabel?: React.ReactNode;
  resetDate?: string;
  unit?: string;
  suffix?: string;
  percent?: number;
  category?: string;
  indicator?: "success" | "error" | null;
  className?: string;
  loading?: boolean;
  showProgressTrack?: boolean;
  showPercentage?: boolean;
};

export function StatCard({
  label,
  value,
  valueTooltip,
  displayValueIsZero = false,
  used,
  limit,
  limitLabel,
  resetDate,
  unit,
  suffix,
  percent,
  category = "All",
  indicator = null,
  className,
  loading,
  showProgressTrack = false,
  showPercentage = true,
}: StatCardProps) {
  const { t } = useTranslation();
  const hasQuotaLimit =
    typeof used === "number" &&
    typeof limit === "number" &&
    Number.isFinite(used) &&
    Number.isFinite(limit) &&
    limit > 0;
  const quotaPercent = hasQuotaLimit
    ? calculateQuotaUsagePercent(used, limit)
    : null;
  const isZeroUsage = used === 0 || displayValueIsZero;
  const progressPercent = quotaPercent ?? percent ?? null;
  const clampedPercent =
    typeof progressPercent === "number"
      ? Math.min(100, Math.max(0, progressPercent))
      : null;
  const hasProgress = clampedPercent !== null;
  const showUsageDetails = showPercentage && !isZeroUsage;
  const showProgress = showUsageDetails && hasProgress;
  const showTrack = showUsageDetails && (hasProgress || showProgressTrack);
  const quotaUsagePercent =
    quotaPercent !== null ? formatQuotaUsagePercent(quotaPercent) : null;

  return (
    <Card className={cn("flex h-full flex-col gap-3 p-5", className)}>
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm text-muted-foreground">{label}</span>
        <Badge variant="default" className="capitalize">
          {category}
        </Badge>
      </div>
      {showTrack && (
        <div
          className="h-1 w-full overflow-hidden rounded-full bg-muted"
          role={showProgress && !loading ? "progressbar" : undefined}
          aria-valuenow={showProgress && !loading ? clampedPercent : undefined}
          aria-valuemin={showProgress && !loading ? 0 : undefined}
          aria-valuemax={showProgress && !loading ? 100 : undefined}
        >
          {showProgress && !loading && (
            <div
              className="h-full rounded-full bg-emerald-400 transition-[width] duration-500 ease-out"
              style={{ width: `${clampedPercent}%` }}
            />
          )}
        </div>
      )}
      {loading ? (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <Skeleton className="h-4 w-full" />
        </div>
      ) : (
        showUsageDetails && (
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{quotaUsagePercent}</span>
            <span>
              {t("usageAndCost.stats.resets")}{" "}
              {resetDate ? new Date(resetDate).toLocaleDateString() : "—"}
            </span>
          </div>
        )
      )}
      <div
        className={cn(
          "flex min-w-0 items-baseline justify-between gap-2",
          !showProgress ? "mt-auto" : "",
        )}
      >
        <div
          className={cn(
            "flex min-w-0 flex-wrap items-baseline",
            unit ? "gap-x-0" : "gap-2",
          )}
        >
          {indicator === "success" && (
            <span
              className="me-2 inline-flex size-4 items-center justify-center rounded-full border-2 border-emerald-400"
              aria-hidden
            />
          )}
          {indicator === "error" && (
            <span
              className="me-2 inline-flex size-4 items-center justify-center rounded-full border-2 border-destructive"
              aria-hidden
            />
          )}
          {loading ? (
            <Skeleton className="h-8 w-24" />
          ) : (
            <span
              title={valueTooltip}
              className={cn(
                "font-semibold tracking-tight text-foreground text-3xl",
              )}
            >
              {value}
            </span>
          )}
          {unit && !loading && (
            <span className="text-sm font-semibold text-foreground">
              {unit}
            </span>
          )}
          {limitLabel !== undefined && !loading && (
            <span className="min-w-0 break-words text-sm font-normal text-muted-foreground">
              /{limitLabel}
              {unit ? ` ${unit}` : ""}
            </span>
          )}
        </div>
        {suffix && !loading && (
          <span className="text-sm text-muted-foreground">{suffix}</span>
        )}
      </div>
    </Card>
  );
}
